/**
 * Per-account live positions debug card.
 *
 * Renders every position the dashboard CURRENTLY THINKS is open on a
 * single leg, sourced from `useDashboardStore.pairs[*].positions` —
 * exactly what the rest of the UI is rendering off, so what you see
 * here is what every other component sees.
 *
 * Why this exists:
 *   - The bot occasionally reports duplicate tickets for the same
 *     symbol/side (rare driver race; observable in audit_log when it
 *     happens) and the dashboard had no place to surface that. The
 *     operator was finding out by squinting at per-symbol PnL drift.
 *   - Sometimes an orphan ticket lingers in the store because the bot
 *     stopped pushing updates but never sent a `close` frame. Showing
 *     the `updatedAt` age makes a stale ghost obvious.
 *
 * Two debug signals are surfaced inline:
 *   • Duplicate badge — multiple open tickets share the same
 *     (symbol, side) on this account. Operator should cross-check the
 *     broker terminal; one is almost certainly an orphan ghost.
 *   • Stale badge   — the row hasn't been touched in >60s. Either the
 *     bot is asleep (expected) or the observer dropped this ticket and
 *     the store is holding a phantom.
 */

import { useMemo, type ReactNode } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useDashboardStore, type PositionLive } from "@/store/dashboard.store";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { fmtMoney } from "@/lib/format";

const STALE_AFTER_MS = 60_000;

interface Props {
  /** Raw account_id (NOT slug) — matches PositionLive.accountId. */
  accountId: string;
  /** Symbol-pair ids the parent already has loaded — we walk each
   *  one's slice in the store and pluck this account's tickets. */
  symbolPairIds: ReadonlyArray<string>;
  className?: string;
}

interface DecoratedPos extends PositionLive {
  isDuplicate: boolean;
  isStale: boolean;
  ageMs: number;
}

export function AccountLivePositions({
  accountId,
  symbolPairIds,
  className,
}: Props): ReactNode {
  const pairBuckets = useDashboardStore((s) => s.pairs);

  const rows = useMemo<DecoratedPos[]>(() => {
    const collected: PositionLive[] = [];
    const seenKeys = new Set<string>();
    for (const pid of symbolPairIds) {
      const slice = pairBuckets[pid]?.positions;
      if (!slice) continue;
      for (const pos of Object.values(slice)) {
        if (pos.accountId !== accountId) continue;
        // Same ticket can appear in two slices if the bot fans the
        // frame to multiple HedgePair DOs. Dedupe — keep the freshest.
        const k = `${pos.accountId}:${pos.ticket}`;
        if (seenKeys.has(k)) {
          const existingIdx = collected.findIndex(
            (p) => `${p.accountId}:${p.ticket}` === k,
          );
          const existing = existingIdx >= 0 ? collected[existingIdx] : undefined;
          if (existing != null && pos.updatedAt > existing.updatedAt) {
            collected[existingIdx] = pos;
          }
          continue;
        }
        seenKeys.add(k);
        collected.push(pos);
      }
    }

    // Detect (symbol, side) duplicates after dedupe by ticket. Two
    // distinct tickets for the same symbol+side on one account is the
    // bug we want to surface.
    const sigCount = new Map<string, number>();
    for (const p of collected) {
      const sig = `${p.symbol}:${p.side}`;
      sigCount.set(sig, (sigCount.get(sig) ?? 0) + 1);
    }

    const now = Date.now();
    return collected
      .map<DecoratedPos>((p) => {
        const sig = `${p.symbol}:${p.side}`;
        return {
          ...p,
          isDuplicate: (sigCount.get(sig) ?? 0) > 1,
          isStale: now - p.updatedAt > STALE_AFTER_MS,
          ageMs: now - p.updatedAt,
        };
      })
      .sort((a, b) => b.updatedAt - a.updatedAt);
  }, [pairBuckets, accountId, symbolPairIds]);

  return (
    <div
      className={cn(
        "min-w-0 overflow-hidden rounded-xl border border-border/40 bg-card/40 p-3 backdrop-blur-sm sm:p-4",
        className,
      )}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-sans text-sm font-medium text-foreground">
          Live Positions
        </h3>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
          <span className="font-mono tabular-nums">{rows.length}</span> open
        </span>
      </div>

      <AnimatePresence mode="wait">
        {rows.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border/40 py-6 text-center"
          >
            <p className="text-xs font-medium text-foreground">No open positions</p>
            <p className="mt-1 text-[10px] text-muted-foreground/70">
              Opens land here the moment the observer reports the first
              ticket on this leg.
            </p>
          </motion.div>
        ) : (
          <motion.div
            key="rows"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.18 }}
            className="overflow-x-auto"
          >
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-1.5 py-1 font-medium">Ticket</th>
                  <th className="px-1.5 py-1 font-medium">Symbol</th>
                  <th className="px-1.5 py-1 font-medium">Side</th>
                  <th className="px-1.5 py-1 text-right font-medium">Lots</th>
                  <th className="px-1.5 py-1 text-right font-medium">Entry</th>
                  <th className="px-1.5 py-1 text-right font-medium">SL</th>
                  <th className="px-1.5 py-1 text-right font-medium">TP</th>
                  <th className="px-1.5 py-1 text-right font-medium">Open PnL</th>
                  <th className="px-1.5 py-1 text-right font-medium">Age</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr
                    key={`${p.accountId}:${p.ticket}`}
                    className={cn(
                      "border-t border-border/30 transition-colors",
                      p.isDuplicate && "bg-destructive/5",
                      p.isStale && !p.isDuplicate && "bg-amber-500/5",
                    )}
                  >
                    <td className="px-1.5 py-1.5 font-mono tabular-nums text-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        {p.ticket}
                        {p.isDuplicate && (
                          <Badge
                            variant="destructive"
                            className="px-1 py-0 text-[9px] uppercase"
                            title="Another ticket has the same symbol+side on this account — one is likely an orphan ghost."
                          >
                            Dup
                          </Badge>
                        )}
                        {p.isStale && (
                          <Badge
                            variant="warning"
                            className="px-1 py-0 text-[9px] uppercase"
                            title="No update in >60s. Bot may be asleep, or this row is a phantom."
                          >
                            Stale
                          </Badge>
                        )}
                      </span>
                    </td>
                    <td className="px-1.5 py-1.5 font-medium text-foreground">
                      {p.symbol}
                    </td>
                    <td
                      className={cn(
                        "px-1.5 py-1.5 font-medium uppercase",
                        p.side === "buy" ? "text-emerald-500" : "text-rose-500",
                      )}
                    >
                      {p.side}
                    </td>
                    <td className="px-1.5 py-1.5 text-right font-mono tabular-nums">
                      {p.lots}
                    </td>
                    <td className="px-1.5 py-1.5 text-right font-mono tabular-nums">
                      {p.entry}
                    </td>
                    <td className="px-1.5 py-1.5 text-right font-mono tabular-nums text-muted-foreground">
                      {p.stopLoss != null ? p.stopLoss : "—"}
                    </td>
                    <td className="px-1.5 py-1.5 text-right font-mono tabular-nums text-muted-foreground">
                      {p.takeProfit != null ? p.takeProfit : "—"}
                    </td>
                    <td
                      className={cn(
                        "px-1.5 py-1.5 text-right font-mono tabular-nums font-medium",
                        p.openPnl > 0 && "text-emerald-500",
                        p.openPnl < 0 && "text-rose-500",
                      )}
                    >
                      {fmtMoney(p.openPnl)}
                    </td>
                    <td className="px-1.5 py-1.5 text-right text-[10px] text-muted-foreground">
                      {formatAge(p.ageMs)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function formatAge(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 1000) return "<1s";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h`;
}
